import {useState} from 'react';

import {useLazyGetFileByIdQuery} from '@dms/api/base';
import {FinancingCompositionScene} from '@dms/api/shared';
import {handleApiError} from '@dms/shared';

import {getSafely, useOnMount} from 'shared/web';

/**
 * Maps image layer fileIds to displayable URLs. Uploads register their URL directly; other fileIds
 * (a pre-existing scene on mount, a generated badge) are resolved through the file storage API.
 * Until a fileId resolves, `getImageUrl` returns null and the layer shows its placeholder.
 */
export function useLayerImageUrls(initialScene: FinancingCompositionScene | null) {
  const [getFileById] = useLazyGetFileByIdQuery();
  const [urlsByFileId, setUrlsByFileId] = useState<Record<string, string>>({});

  const registerImageUrl = (fileId: string, url: string) =>
    setUrlsByFileId((current) => ({...current, [fileId]: url}));

  const resolveImageUrl = (fileId: string) =>
    getFileById({fileId}, true)
      .unwrap()
      .then((file) => registerImageUrl(fileId, file.originalUri))
      .catch(handleApiError);

  useOnMount(() => {
    initialScene?.layers.forEach((layer) => {
      if (layer.type !== 'image') {
        return;
      }
      resolveImageUrl(layer.fileId);
    });
  });

  const getImageUrl = (fileId: string) => getSafely(urlsByFileId, fileId) ?? null;

  return {getImageUrl, registerImageUrl, resolveImageUrl};
}
